import {pushAnalyticsEvent} from '../analytics'
import {getMarketingProductAttributes} from './getMarketingProductAttributes'

export const GA4_AFFILIATION = 'Cavenders'

export const GA4_BUSINESS_VERTICAL = 'retail'

export const GA4_DEFAULT_CURRENCY = 'USD'

export const orUndefined = (value) => {
    if (value === null || value === undefined) return undefined
    if (typeof value === 'string' && value.trim() === '') return undefined
    if (typeof value === 'number' && !Number.isFinite(value)) return undefined
    return value
}

export const toAmount = (value) => {
    if (value === null || value === undefined || value === '') return undefined
    const parsed =
        typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(parsed)) return undefined
    if (Number.isInteger(parsed)) return parsed
    const shifted = Math.round(Number(parsed + 'e2'))
    if (Number.isFinite(shifted)) {
        const rounded = Number(shifted + 'e-2')
        if (Number.isFinite(rounded)) return rounded
    }
    return Math.round(parsed * 100) / 100
}

export const toCount = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
    if (typeof value !== 'string' || value.trim() === '') return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

export const getCategoryFields = (categoryNames) => {
    const names = (
        Array.isArray(categoryNames) ? categoryNames : String(categoryNames || '').split('/')
    )
        .map((name) => (typeof name === 'string' ? name.trim() : name))
        .filter(Boolean)

    return {
        item_category: orUndefined(names[0]),
        item_category2: orUndefined(names[1]),
        item_category3: orUndefined(names[2]),
        item_category4: orUndefined(names[3]),
        item_category5: orUndefined(names[4])
    }
}

export const buildItem = ({
    itemId,
    itemName,
    index,
    brand,
    categoryNames,
    variant,
    price,
    discount,
    quantity,
    coupon,
    listId,
    listName,
    locationId,
    creativeName,
    creativeSlot,
    promotionId,
    promotionName
} = {}) => {
    const item = {
        item_id: orUndefined(itemId),
        item_name: orUndefined(itemName),
        affiliation: GA4_AFFILIATION,
        coupon: orUndefined(coupon),
        discount: toAmount(discount),
        index: toCount(index),
        item_brand: orUndefined(brand),
        ...getCategoryFields(categoryNames),
        item_list_id: orUndefined(listId),
        item_list_name: orUndefined(listName),
        item_variant: orUndefined(variant),
        location_id: orUndefined(locationId),
        price: toAmount(price),
        google_business_vertical: GA4_BUSINESS_VERTICAL,
        quantity: toCount(quantity)
    }

    if (creativeName != null) item.creative_name = orUndefined(creativeName)
    if (creativeSlot != null) item.creative_slot = orUndefined(creativeSlot)
    if (promotionId != null) item.promotion_id = orUndefined(promotionId)
    if (promotionName != null) item.promotion_name = orUndefined(promotionName)

    return item
}

export const getLineItemUnitPrice = (lineItem) => {
    const lineTotal =
        lineItem?.priceAfterOrderDiscount ?? lineItem?.priceAfterItemDiscount ?? lineItem?.price
    const amount = toAmount(lineTotal)
    if (amount === undefined) return undefined
    return toAmount(amount / (lineItem?.quantity || 1))
}

export const getLineItemUnitDiscount = (lineItem) => {
    const basePrice = toAmount(lineItem?.basePrice)
    const unitPrice = getLineItemUnitPrice(lineItem)
    if (basePrice === undefined || unitPrice === undefined) return undefined
    const discount = toAmount(basePrice - unitPrice)
    return discount > 0 ? discount : undefined
}

export const sumItemsValue = (items) =>
    toAmount(
        (items || []).reduce((total, item) => total + (item?.price || 0) * (item?.quantity ?? 0), 0)
    )

const ensureDataLayer = () => {
    if (typeof window === 'undefined') return null
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') {
        window.dataLayer = []
    }
    return window.dataLayer
}

export const pushEcommerceEvent = (eventName, ecommerce, extra = {}) => {
    const dataLayer = ensureDataLayer()
    if (!dataLayer || !eventName) return
    dataLayer.push({ecommerce: null})
    dataLayer.push({event: eventName, ecommerce, ...extra})
}

export const clearEcommerce = () => {
    const dataLayer = ensureDataLayer()
    if (!dataLayer) return
    dataLayer.push({ecommerce: null})
}

export const pushUserEvent = async (eventName, customer, params = {}) => {
    try {
        clearEcommerce()
        await pushAnalyticsEvent({
            eventName,
            userId: customer?.customerId || '',
            email: customer?.email || '',
            registered: !!customer?.isRegistered,
            phone: customer?.phoneMobile || customer?.phoneHome || '',
            extraData: params
        })
    } catch {
        // empty catch block
    }
}

export const toGA4Items = (dataLayerProducts = [], options = {}) => {
    const {listId, listName} = options

    return (dataLayerProducts || []).map((product, index) => ({
        ...buildItem({
            itemId: product?.id,
            itemName: product?.name,
            index,
            brand: product?.brand,
            categoryNames: product?.categoryNames ?? product?.category,
            variant: product?.variant,
            price: product?.unitPrice,
            discount: product?.unitDiscount,
            quantity: product?.quantity,
            coupon: product?.coupon,
            listId,
            listName
        }),
        ...getMarketingProductAttributes(product)
    }))
}
